import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  LayoutGrid, FileImage, Megaphone, Users, Briefcase, 
  FileText, Newspaper, FlaskConical, Leaf, Globe, Link2,
  Activity, Clock, CheckCircle, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statCards = [
  { id: 'banners', label: 'Banners', icon: FileImage, color: 'bg-blue-500' },
  { id: 'announcements', label: 'Announcements', icon: Megaphone, color: 'bg-amber-500' },
  { id: 'varieties', label: 'Varieties', icon: Leaf, color: 'bg-green-500' },
  { id: 'technologies', label: 'Technologies', icon: FlaskConical, color: 'bg-purple-500' },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, color: 'bg-indigo-500' },
  { id: 'tenders', label: 'Tenders', icon: FileText, color: 'bg-rose-500' },
  { id: 'news_events', label: 'News & Events', icon: Newspaper, color: 'bg-cyan-500' },
  { id: 'research', label: 'Research', icon: FlaskConical, color: 'bg-orange-500' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('cish_token');
      
      // Fetch counts for each content type
      const statsPromises = statCards.map(async (card) => {
        try {
          const response = await fetch(`${API_URL}/api/${card.id}?published_only=false`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            return { [card.id]: Array.isArray(data) ? data.length : 0 };
          }
          return { [card.id]: 0 };
        } catch {
          return { [card.id]: 0 };
        }
      });

      const results = await Promise.all(statsPromises);
      const statsObj = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setStats(statsObj);

      // Fetch recent audit logs
      const auditResponse = await fetch(`${API_URL}/api/audit/logs?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (auditResponse.ok) {
        const auditData = await auditResponse.json();
        setRecentActivity(auditData.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'update': return <Activity className="w-4 h-4 text-blue-500" />;
      case 'delete': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">
            Welcome, {user?.name}!
          </h1>
          <p className="text-gray-500 mt-1">
            Role: <span className="font-medium text-[#0E6D2B] capitalize">{user?.role}</span>
            {user?.department && (
              <> | Department: <span className="font-medium">{user.department}</span></>
            )}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {loading ? '-' : (stats[card.id] || 0)}
                    </p>
                  </div>
                  <div className={`${card.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <a href="/admin/banners" className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-center">
                <FileImage className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-sm font-medium">Manage Banners</p>
              </a>
              <a href="/admin/announcements" className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-center">
                <Megaphone className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p className="text-sm font-medium">Announcements</p>
              </a>
              <a href="/admin/jobs" className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-center">
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
                <p className="text-sm font-medium">Post Jobs</p>
              </a>
              <a href="/admin/news" className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-center">
                <Newspaper className="w-8 h-8 mx-auto mb-2 text-cyan-500" />
                <p className="text-sm font-medium">News & Events</p>
              </a>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Activity
            </h2>
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-sm">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((log, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                    {getActionIcon(log.action)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">
                        <span className="font-medium capitalize">{log.action}</span>
                        {' '}{log.resource_type}
                      </p>
                      <p className="text-xs text-gray-500">
                        {log.actor_email} • {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Government Compliance Badge */}
        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-800">GIGW 3.0 Compliant Admin Panel</p>
              <p className="text-xs text-blue-600">Following Guidelines for Indian Government Websites | NIC Standards</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
